
// src/app/pages/home/home.component.ts

import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { IssuesService, Issue } from '../../services/issues.service';
import { PressReleaseService } from '../../services/press-release.service';
import { createPressReleaseSlug, PressRelease } from '../../models/press-release';
import { EmailJsService } from '../../services/emailjs.service';
import { CountdownComponent } from '../../components/countdown/countdown.component';
import { SeoService } from '../../services/seo.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CountdownComponent
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit {

  signatures = 8000;
  goal = 15000;
  raised = 6000;
  goalAmount = 250000;

  petitionForm: FormGroup;
  volunteerForm: FormGroup;

  issues: Issue[] = [];
  latestReleases: PressRelease[] = [];

  petitionSubmitted = false;
  volunteerSubmitted = false;
  isSubmitting = false;
  errorMessage = '';
  isLoading = true;

  constructor(
    private issuesService: IssuesService,
    private pressReleaseService: PressReleaseService,
    private fb: FormBuilder,
    private emailJsService: EmailJsService,
    private cdr: ChangeDetectorRef,
    private seo: SeoService
  ) {
    this.petitionForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      zip: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]]
    });

    this.volunteerForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      zip: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      role: ['']
    });
  }

  isOptInFlagIssue(issue: Issue): boolean {
    return /medicaid|healthcare|social security/i.test(issue.title);
  }

  getReleaseSlug(release: PressRelease): string {
    return createPressReleaseSlug(release);
  }

  ngOnInit(): void {
    this.seo.setHome();

    /*
     * SITE-WIDE ENTITY GRAPH
     *
     * These three entities form the foundation of the website:
     *
     * 1. Lydia Christensen
     *    https://www.lydialynnchristensen.com/#lydia-christensen
     *
     * 2. Lydia Christensen for United States Senate
     *    https://www.lydialynnchristensen.com/#campaign
     *
     * 3. Official website
     *    https://www.lydialynnchristensen.com/#website
     *
     * Other pages will reference these same @id values rather than
     * creating duplicate Person or Organization entities.
     */
    this.seo.setStructuredData({
      "@context": "https://schema.org",
      "@graph": [

        /*
         * OFFICIAL WEBSITE
         */
        {
          "@type": "WebSite",
          "@id": this.seo.websiteId,
          "url": "https://www.lydialynnchristensen.com/",
          "name": "Lydia Christensen for U.S. Senate",
          "description": "Official campaign website for Lydia Christensen, an American politician from Michigan and a candidate for the United States Senate in 2026.",
          "about": {
            "@id": this.seo.lydiaId
          },
          "publisher": {
            "@id": this.seo.campaignId
          }
        },

        /*
         * LYDIA CHRISTENSEN — PRIMARY PERSON ENTITY
         */
        {
          "@type": "Person",
          "@id": this.seo.lydiaId,
          "name": "Lydia Christensen",
          "url": "https://www.lydialynnchristensen.com/",
          "image": [
            "https://www.lydialynnchristensen.com/assets/lydia-christensen.jpg",
            "https://www.lydialynnchristensen.com/assets/lydia-christensen-campaign.jpg",
            "https://www.lydialynnchristensen.com/assets/lydia-christensen-gothic.jpg",
            "https://www.lydialynnchristensen.com/assets/lydia-christensen-nicer.jpg",
            "https://www.lydialynnchristensen.com/assets/lydia-christensen-puppy.jpg"
          ],
          "jobTitle": "Candidate for the United States Senate",
          "description": "Lydia Christensen is an American politician from Michigan and a candidate for the United States Senate in 2026.",
          "memberOf": {
            "@id": this.seo.campaignId
          },
          "sameAs": [
            "https://www.facebook.com/lydiaforussenate",
            "https://x.com/lydia4ussenate",
            "https://www.tiktok.com/@lydiaforussenate",
            "https://bsky.app/profile/lydiaforussenate.bsky.social",
            "https://www.youtube.com/@LydiaChristensen",
            "https://www.linkedin.com/in/lydialynn"
          ]
        },

        /*
         * CAMPAIGN ORGANIZATION
         */
        {
          "@type": "Organization",
          "@id": this.seo.campaignId,
          "name": "Lydia Christensen for United States Senate",
          "url": "https://www.lydialynnchristensen.com/",
          "description": "Principal campaign committee for Lydia Christensen's 2026 campaign for United States Senate in Michigan.",
          "sameAs": [
            "https://www.fec.gov/data/committee/C00903575/"
          ]
        }

      ]
    });

    /*
     * LOAD PRESS RELEASES
     */
    this.pressReleaseService.getPressReleases().subscribe(releases => {
      this.latestReleases = releases.slice(0, 3);
    });

    /*
     * LOAD ISSUES
     */
    this.issuesService.fetchIssues();

    this.issuesService.getIssues().subscribe({
      next: (issues: Issue[]) => {
        this.issues = issues;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading home issues', err);
        this.isLoading = false;
      }
    });
  }

  onPetitionSubmit(): void {
    if (this.petitionForm.valid) {
      this.signatures += 1;
      this.petitionSubmitted = true;
      this.petitionForm.reset();
    }
  }

  async onVolunteerSubmit(): Promise<void> {
    if (this.volunteerForm.invalid) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.volunteerSubmitted = false;

    try {
      const formData = {
        ...this.volunteerForm.value,
        message: 'Volunteer form submission'
      };

      await this.emailJsService.sendEmail(
        formData,
        'Volunteer'
      );

      this.volunteerSubmitted = true;
      this.volunteerForm.reset();

    } catch (error: any) {
      this.errorMessage =
        'Failed to send. Please try again or email senator@lydialynnchristensen.com.';

      console.error('Volunteer email error:', error);

    } finally {
      this.isSubmitting = false;
    }
  }

  resetPetitionForm(): void {
    this.petitionSubmitted = false;
    this.petitionForm.reset();
  }

  resetVolunteerForm(): void {
    this.volunteerSubmitted = false;
    this.volunteerForm.reset();
    this.errorMessage = '';
  }
}
